import { TCustomerColors } from './GlobalCustomization'
import { TOrganizationalUnitScopeType } from './GlobalOrganizationalUnit'

export enum PeriodType {
  QUARTERLY = 'QUARTERLY',
  MONTHLY = 'MONTHLY',
  MANUALLY = 'MANUALLY'
}

export type TPatchEconomy = {
  period_type?: PeriodType | null
  latest_closed_period?: string | null
}

export type TPatchEconomyResponse = {
  period_type: PeriodType | null
  latest_closed_period: string | null
}

export enum FrequencyOfDataSynchronization {
  ONCE_PER_DAY = 'ONCE_PER_DAY',
  SEVERAL_TIMES_PER_DAY = 'SEVERAL_TIMES_PER_DAY'
}

export const translateFrequencyOfDataSynchronization: Record<
  FrequencyOfDataSynchronization,
  { display: string; value: FrequencyOfDataSynchronization }
> = {
  [FrequencyOfDataSynchronization.ONCE_PER_DAY]: {
    display: '1 gång per dygn',
    value: FrequencyOfDataSynchronization.ONCE_PER_DAY
  },
  [FrequencyOfDataSynchronization.SEVERAL_TIMES_PER_DAY]: {
    display: 'Flera gånger per dygn',
    value: FrequencyOfDataSynchronization.SEVERAL_TIMES_PER_DAY
  }
}

export enum License {
  INSIGHT = 'INSIGHT',
  PRO = 'PRO',
  STARTER = 'STARTER',
  DEDU_FREEMIUM = 'DEDU_FREEMIUM',
  DH_FREEMIUM = 'DH_FREEMIUM',
  OTHER = 'OTHER'
}

export const translateLicense: Record<
  License,
  { display: string; value: License }
> = {
  [License.INSIGHT]: {
    display: 'Insight',
    value: License.INSIGHT
  },
  [License.PRO]: {
    display: 'Pro',
    value: License.PRO
  },
  [License.STARTER]: {
    display: 'Starter',
    value: License.STARTER
  },
  [License.DEDU_FREEMIUM]: {
    display: 'DeDU Freemium',
    value: License.DEDU_FREEMIUM
  },
  [License.DH_FREEMIUM]: {
    display: 'DH Freemium',
    value: License.DH_FREEMIUM
  },
  [License.OTHER]: {
    display: 'Övrig',
    value: License.OTHER
  }
}

export enum SupportLevel {
  CHAT_AND_EMAIL = 'CHAT_AND_EMAIL',
  CUSTOMER_SUCCESS = 'CUSTOMER_SUCCESS',
  SLA = 'SLA'
}

export const translateSupportLevel: Record<
  SupportLevel,
  { display: string; value: SupportLevel }
> = {
  [SupportLevel.CHAT_AND_EMAIL]: {
    display: 'Chat och email',
    value: SupportLevel.CHAT_AND_EMAIL
  },
  [SupportLevel.CUSTOMER_SUCCESS]: {
    display: 'Customer success',
    value: SupportLevel.CUSTOMER_SUCCESS
  },
  [SupportLevel.SLA]: {
    display: 'SLA',
    value: SupportLevel.SLA
  }
}

export type AllowAcess = {
  white_label_colors: boolean
  white_label_logotype: boolean
  insight_studio: boolean
  access_hub: boolean
  sso: boolean
  frequency_of_data_synchronization: FrequencyOfDataSynchronization | null
  support_level: SupportLevel | null
  embed: boolean
  export_tables: boolean
  allowed_historical_time_period: number
  allow_ai_content_chat: boolean
  allow_currency_converter: boolean
  allow_financial_year: boolean
  allow_key_metrics: boolean
  allow_mcp: boolean
  allow_reports: boolean
  allow_analyses: boolean
  allow_errand_ai_safety_measures: boolean
  allow_errand_ai_error_source: boolean
  allow_errand_ai_water_leak: boolean
  allow_ticket_ai_safety_measures: boolean
  allow_ticket_ai_error_source: boolean
  allow_ticket_ai_water_leak: boolean
  internal_follow_up: boolean
}

export enum Segment {
  MunicipalHousingCompany = 'MunicipalHousingCompany',
  RealEstateInvestmentFund = 'RealEstateInvestmentFund',
  MunicipalCommercialRealEstateCompany = 'MunicipalCommercialRealEstateCompany',
  PrivateHousingCompany = 'PrivateHousingCompany',
  PrivateCommercialCompany = 'PrivateCommercialCompany',
  PropertyManagementCompany = 'PropertyManagementCompany'
}

export const translateSegment: Record<Segment, { display: string }> = {
  [Segment.MunicipalHousingCompany]: {
    display: 'Kommunalt bostadsbolag'
  },
  [Segment.RealEstateInvestmentFund]: {
    display: 'Fondbolag'
  },
  [Segment.MunicipalCommercialRealEstateCompany]: {
    display: 'Kommunal kommersiell'
  },
  [Segment.PrivateHousingCompany]: {
    display: 'Privat bostadsbolag'
  },
  [Segment.PropertyManagementCompany]: {
    display: 'Förvaltningsbolag'
  },
  [Segment.PrivateCommercialCompany]: {
    display: 'Privat kommersiell'
  }
}

export enum Country {
  sv = 'sv'
}

export const translateCountry: Record<Country, { display: string }> = {
  [Country.sv]: {
    display: 'Sverige'
  }
}

/**
 * Whether a customer registers felanmälningar as ärenden or as
 * arbetsordrar. It decides which variant of an analysis type they are
 * offered; the two count the same thing from different source tables.
 *
 * Widening this means an ENUM migration on both customers and
 * analysis_type_variants, so the UI must never offer a third value.
 */
export enum MaintenanceRequestSource {
  ERRAND = 'ERRAND',
  WORK_ORDER = 'WORK_ORDER'
}

export const translateMaintenanceRequestSource: Record<
  MaintenanceRequestSource,
  { display: string }
> = {
  [MaintenanceRequestSource.ERRAND]: { display: 'Ärenden' },
  [MaintenanceRequestSource.WORK_ORDER]: { display: 'Arbetsordrar' }
}

export enum BudgetMonth {
  JANUARY = 'JANUARY',
  FEBRUARY = 'FEBRUARY',
  MARCH = 'MARCH',
  APRIL = 'APRIL',
  MAY = 'MAY',
  JUNE = 'JUNE',
  JULY = 'JULY',
  AUGUST = 'AUGUST',
  SEPTEMBER = 'SEPTEMBER',
  OCTOBER = 'OCTOBER',
  NOVEMBER = 'NOVEMBER',
  DECEMBER = 'DECEMBER'
}

export const translateBudgetMonth: Record<BudgetMonth, { display: string }> = {
  [BudgetMonth.JANUARY]: { display: 'Januari' },
  [BudgetMonth.FEBRUARY]: { display: 'Februari' },
  [BudgetMonth.MARCH]: { display: 'Mars' },
  [BudgetMonth.APRIL]: { display: 'April' },
  [BudgetMonth.MAY]: { display: 'Maj' },
  [BudgetMonth.JUNE]: { display: 'Juni' },
  [BudgetMonth.JULY]: { display: 'Juli' },
  [BudgetMonth.AUGUST]: { display: 'Augusti' },
  [BudgetMonth.SEPTEMBER]: { display: 'September' },
  [BudgetMonth.OCTOBER]: { display: 'Oktober' },
  [BudgetMonth.NOVEMBER]: { display: 'November' },
  [BudgetMonth.DECEMBER]: { display: 'December' }
}

export type Meta = {
  segment: Segment | null
  country: Country | null
}

export type ContactPerson = {
  name: string | null
  email: string | null
  phone: string | null
}

export type ContactPersons = {
  agreement_owner: ContactPerson
  system_owner: ContactPerson
  pub_owner: ContactPerson
}

export type InternalCustomer = {
  id: string
  name: string
  number_of_dashboards: number | null
  number_of_dashboards_in_use: number
  contract_from: Date
  data_from: Date
  license: License | null
  is_demo: boolean
  using_demo_data: boolean
  using_homepal_star_schema: boolean
  is_freemium: boolean
  is_management_company: boolean
  allow_access: AllowAcess
  period_type: PeriodType | null
  latest_closed_period: string | null
  integrate_company_groups: boolean
  meta: Meta
  contact_persons: ContactPersons
  billed_sqm: number | null
  budget_month: BudgetMonth | null
  link_to_drive: string | null
  allow_financial_year: boolean
  allow_key_metrics: boolean
  allow_mcp: boolean
  allow_reports: boolean
  allow_ai_content_chat: boolean
  allow_analyses: boolean
  maintenance_request_source: MaintenanceRequestSource
  allow_errand_ai_safety_measures: boolean
  allow_errand_ai_error_source: boolean
  allow_errand_ai_water_leak: boolean
  allow_ticket_ai_safety_measures: boolean
  allow_ticket_ai_error_source: boolean
  allow_ticket_ai_water_leak: boolean
  allow_normalized_rent: boolean
  object_size_field: string | null
  organizational_unit_access_type: 'real_estate' | 'company'
  test_environment_enabled: boolean
}

/** One of our customers as a picker option, by name. */
export type InternalCustomerOption = {
  id: string
  title: string
}

export interface PatchInternalCustomerBody {
  data: {
    access_hub?: boolean
    contract_from?: Date
    data_from?: Date
    embed?: boolean
    export_tables?: boolean
    frequency_of_data_synchronization?: FrequencyOfDataSynchronization
    allowed_historical_time_period?: number
    number_of_dashboards?: string | null
    insight_studio?: boolean
    license?: License
    is_demo?: boolean
    using_homepal_star_schema?: boolean
    is_management_company?: boolean
    sso?: boolean
    support_level?: SupportLevel
    white_label_colors?: boolean
    white_label_logotype?: boolean
    period_type?: PeriodType
    integrate_company_groups?: boolean
    allow_financial_year?: boolean
    allow_key_metrics?: boolean
    allow_mcp?: boolean
    allow_reports?: boolean
    allow_ai_content_chat?: boolean
    allow_analyses?: boolean
    maintenance_request_source?: MaintenanceRequestSource
    allow_errand_ai_safety_measures?: boolean
    allow_errand_ai_error_source?: boolean
    allow_currency_converter?: boolean
    allow_errand_ai_water_leak?: boolean
    allow_ticket_ai_safety_measures?: boolean
    allow_ticket_ai_error_source?: boolean
    allow_ticket_ai_water_leak?: boolean
    internal_follow_up?: boolean
    segment?: Segment
    country?: Country
    agreement_owner_name?: string | null
    agreement_owner_email?: string | null
    agreement_owner_phone?: string | null
    system_owner_name?: string | null
    system_owner_email?: string | null
    system_owner_phone?: string | null
    pub_owner_name?: string | null
    pub_owner_email?: string | null
    pub_owner_phone?: string | null
    budget_month?: BudgetMonth | null
    link_to_drive?: string | null
    allow_normalized_rent?: boolean
    object_size_field?: string | null
    organizational_unit_access_type?: 'real_estate' | 'company'
    test_environment_enabled?: boolean
  }
}

export type Customer = {
  customer_id: string
  name: string
  slug: string
  technical_name: string
  allow_access: AllowAcess
  colors: TCustomerColors
  has_idp: boolean
  is_demo: boolean
  is_management_company: boolean
  latest_job_run?: string | null
  number_of_dashboards_in_use: number
  number_of_dashboards: number | null
  period_type: PeriodType | null
  latest_closed_period: string | null
  integrate_company_groups: boolean
  organizational_unit_access_type: TOrganizationalUnitScopeType
}
